export const LIMIT_CATEGORIES = [
  'all', 'users', 'crm', 'automation', 'email', 'files', 'ai',
  'conversations', 'connections', 'analytics', 'security', 'other',
];

export const CATEGORY_LABELS = {
  all: 'All limits',
  users: 'Users',
  crm: 'CRM',
  automation: 'Automation',
  email: 'Email',
  files: 'Files & storage',
  ai: 'AI',
  conversations: 'Conversations',
  connections: 'Connections',
  analytics: 'Analytics',
  security: 'Security',
  other: 'Other',
};

export const LICENSE_PRODUCTS = Object.freeze([
  { id: 'platform_core', label: 'Platform Core', description: 'People and organisation capacity.', sortOrder: 10 },
  { id: 'crm', label: 'CRM', description: 'Customer data model and CRM capacity.', sortOrder: 20 },
  { id: 'automation', label: 'Flow & Automation', description: 'Flows, automations and scheduled work.', sortOrder: 30 },
  { id: 'files', label: 'Files & Storage', description: 'Storage and upload capacity.', sortOrder: 40 },
  { id: 'email', label: 'Email', description: 'Outbound email allowances.', sortOrder: 50 },
  { id: 'docflow', label: 'DocFlow', description: 'Documents, templates and recipient capacity.', sortOrder: 60 },
  { id: 'integrations', label: 'API & Integrations', description: 'API and webhook traffic.', sortOrder: 70 },
  { id: 'data_manager', label: 'Data Manager', description: 'Data quality, matching and cleanup capacity.', sortOrder: 80 },
  { id: 'cluvik_ai', label: 'Cluvik AI', description: 'AI-assisted work across Cluvik.', sortOrder: 90 },
  { id: 'ai_calling', label: 'AI Calling', description: 'AI voice calls and connected minutes.', sortOrder: 100 },
  { id: 'other', label: 'Other products', description: 'Additional published allowances.', sortOrder: 999 },
]);

const LICENSE_PRODUCT_IDS = new Set(LICENSE_PRODUCTS.map((product) => product.id));
const LICENSE_FAMILY_PREFIXES = Object.freeze([
  ['docflow.', 'docflow'],
  ['webhook.', 'integrations'],
  ['api.', 'integrations'],
  ['email.', 'email'],
  ['storage.', 'files'],
  ['files.', 'files'],
  ['users.', 'platform_core'],
  ['crm.', 'crm'],
  ['automation.', 'automation'],
  ['data_manager.', 'data_manager'],
  ['ai_calling.', 'ai_calling'],
  ['ai.', 'cluvik_ai'],
]);
const CATEGORY_TO_LICENSE = Object.freeze({
  users: 'platform_core', identity: 'platform_core', crm: 'crm', records: 'crm',
  automation: 'automation', workflow: 'automation', files: 'files', storage: 'files',
  email: 'email', connections: 'integrations', integrations: 'integrations', api: 'integrations',
  ai: 'cluvik_ai', cluvik_ai: 'cluvik_ai', conversations: 'other', analytics: 'other',
  security: 'other',
});

const normaliseLicenseFamily = (row, key) => {
  const explicit = String(row.license_family || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (LICENSE_PRODUCT_IDS.has(explicit)) return explicit;
  const prefix = LICENSE_FAMILY_PREFIXES.find(([candidate]) => key.startsWith(candidate));
  if (prefix) return prefix[1];
  return CATEGORY_TO_LICENSE[String(row.category || '').toLowerCase()] || 'other';
};

const STATUS = {
  healthy: { label: 'Healthy', tone: 'success' },
  approaching_limit: { label: 'Approaching limit', tone: 'warning' },
  near_limit: { label: 'Near limit', tone: 'warning' },
  limit_reached: { label: 'Limit reached', tone: 'danger' },
  over_limit: { label: 'Over limit', tone: 'danger' },
  unlimited: { label: 'Unlimited', tone: 'neutral' },
  not_entitled: { label: 'Not entitled', tone: 'muted' },
  not_metered: { label: 'Not metered', tone: 'muted' },
  usage_delayed: { label: 'Usage delayed', tone: 'warning' },
};

const SOURCE_LABELS = {
  platform_ceiling: 'Platform safety ceiling',
  platform_safety_ceiling: 'Platform safety ceiling',
  safety_restriction: 'Temporary safety restriction',
  temporary_safety_restriction: 'Temporary safety restriction',
  tenant_override: 'Tenant override',
  admin_override: 'Tenant override',
  add_on: 'Purchased add-on',
  commercial_add_on: 'Purchased add-on',
  template: 'Template',
  catalogue_default: 'Catalogue default',
};

export const normaliseQuota = (row = {}) => {
  const key = row.quota_key || row.key || row.limit_key;
  const licenseFamily = normaliseLicenseFamily(row, key || '');
  const product = LICENSE_PRODUCTS.find((candidate) => candidate.id === licenseFamily);
  const effective = row.effective_limit ?? row.effective ?? row.limit ?? null;
  const usage = row.usage ?? row.used ?? row.consumed_value ?? null;
  const template = row.template_limit ?? row.original_limit ?? row.included ?? null;
  const addOn = row.add_on_allowance ?? row.add_on ?? row.purchased ?? 0;
  const override = row.tenant_override ?? row.override ?? null;
  const unlimited = effective === -1 || row.unlimited === true;
  const remaining = unlimited
    ? null
    : row.remaining ?? (effective != null && usage != null ? Math.max(0, effective - usage) : null);
  const percentage = row.percent ?? (unlimited || effective == null || usage == null || effective <= 0
    ? null
    : Math.round((usage / effective) * 1000) / 10);
  const derivedStatus = (
    unlimited ? 'unlimited'
      : usage == null ? 'not_metered'
        : effective != null && usage > effective ? 'over_limit'
          : effective != null && usage === effective ? 'limit_reached'
            : percentage >= 90 ? 'near_limit'
              : percentage >= 75 ? 'approaching_limit'
                : 'healthy'
  );
  const statusValue = String(row.status || '').toLowerCase().replaceAll(' ', '_');
  const displayStatusValue = String(row.display_status || '').toLowerCase().replaceAll(' ', '_');
  // Older resolvers used one ambiguous `exceeded` status for both equality
  // and true overage. Prefer the canonical display status when present, then
  // derive from usage/effective so filtering and severity never collapse.
  const rawStatus = statusValue === 'exceeded'
    ? (['limit_reached', 'over_limit'].includes(displayStatusValue) ? displayStatusValue : derivedStatus)
    : (statusValue || derivedStatus);

  return {
    ...row,
    key,
    name: row.name || row.label || row.quota_key || row.limit_key,
    category: (row.category || 'other').toLowerCase(),
    licenseFamily,
    licenseLabel: row.license_label || product?.label || 'Other products',
    licenseDescription: row.license_description || product?.description || null,
    licenseSortOrder: Number.isFinite(Number(row.license_sort_order))
      ? Number(row.license_sort_order) : product?.sortOrder || 999,
    scope: row.scope || 'tenant',
    scopeId: row.scope_id || null,
    subjectRequired: row.subject_required === true,
    overrideId: row.override_details?.id || row.override_id || null,
    unit: row.unit || 'count',
    window: row.window || row.period || row.reset_cycle || 'capacity',
    usage,
    templateLimit: template,
    addOn,
    override,
    effective,
    remaining,
    percentage,
    unlimited,
    source: row.source || row.provenance?.source || 'catalogue_default',
    sourceId: row.source_id || row.provenance?.source_id || null,
    templateName: row.template_name || row.template?.name || row.provenance?.template_name || null,
    templateVersion: row.template_version || row.template?.version || row.provenance?.template_version || null,
    // Policy intent is not runtime proof. A quota may be configured as a
    // future hard stop while Platform truthfully reports it as declared-only.
    // Prefer that lifecycle signal so Admin never overstates enforcement.
    enforcement: row.runtime_status || row.enforcement_status
      || row.enforcement_mode || row.enforcement || 'declared_only',
    resetAt: row.reset_at || row.reset_time || null,
    effectiveFrom: row.effective_from || null,
    expiresAt: row.expires_at || row.expiry || null,
    status: rawStatus,
    statusLabel: STATUS[rawStatus]?.label || row.display_status || rawStatus.replaceAll('_', ' '),
    statusTone: STATUS[rawStatus]?.tone || 'neutral',
  };
};

export const normaliseQuotas = (payload) => {
  const rows = Array.isArray(payload)
    ? payload
    : payload?.quotas || payload?.limits || payload?.items || [];
  return rows.map(normaliseQuota);
};

export const availableLicenseProducts = (quotas = []) => LICENSE_PRODUCTS.filter(
  (product) => quotas.some((quota) => quota.licenseFamily === product.id),
);

export const catalogRows = (payload) => (Array.isArray(payload)
  ? payload
  : payload?.items || payload?.catalog || payload?.quotas || []);

/**
 * Platform persists template allowances as Dict[str, int]. The catalogue owns
 * labels, category, scope, period and runtime metadata; joining them here keeps
 * the editing row model presentational and sends no copied metadata back.
 */
export const templateLimitRows = (template = {}, catalogPayload = []) => {
  const limits = template?.limits || template?.quotas || {};
  const catalog = catalogRows(catalogPayload);
  const byKey = Object.fromEntries(catalog.map((row) => [row.key || row.quota_key, row]));
  if (Array.isArray(limits)) {
    return limits.map((row) => {
      const key = row.key || row.quota_key;
      return { ...byKey[key], ...row, key, quota_key: key, value: row.value ?? row.limit };
    });
  }
  return Object.entries(limits || {}).map(([key, value]) => ({
    ...(byKey[key] || {}), key, quota_key: key, value,
  }));
};

export const templateLimitsPayload = (rows, values = {}) => Object.fromEntries(
  rows.map((row) => {
    const key = row.key || row.quota_key;
    return [key, Number(values[key] ?? row.value)];
  }),
);

export const normaliseHistoryEvent = (event = {}) => ({
  ...event,
  timestamp: event.timestamp || event.created_at || event.occurred_at || null,
  actor: event.actor_name || event.actor_email || event.actor_user_id || event.actor_id || 'System',
  reason: event.reason ?? event.after?.reason ?? event.details?.reason ?? null,
  reference: event.reference ?? event.after?.reference ?? event.details?.reference ?? null,
});

export const sourceLabel = (source) => SOURCE_LABELS[source] || source?.replaceAll('_', ' ') || 'Unknown';

export const formatValue = (value, unit = 'count') => {
  if (value === -1) return 'Unlimited';
  if (value == null) return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  if (unit.toLowerCase() === 'bytes') {
    if (number >= 1024 ** 3) return `${(number / 1024 ** 3).toFixed(1)} GB`;
    if (number >= 1024 ** 2) return `${(number / 1024 ** 2).toFixed(1)} MB`;
    return `${number.toLocaleString()} bytes`;
  }
  if (unit.toLowerCase().includes('gb')) return `${number.toLocaleString()} GB`;
  if (unit.toLowerCase().includes('minute')) return `${number.toLocaleString()} min`;
  return number.toLocaleString();
};

export const formatWindow = (window) => {
  const key = String(window || 'capacity').toLowerCase();
  return ({
    capacity: 'No reset', never: 'No reset', minute: 'Per minute', hourly: 'Per hour',
    hour: 'Per hour', daily: 'Per day', day: 'Per day', monthly: 'Per month',
    month: 'Per month', rolling: 'Rolling period',
  })[key] || window;
};

export const lowerImpact = (quota, proposed) => {
  const usage = Number(quota?.usage || 0);
  const value = Number(proposed);
  const excess = Number.isFinite(value) && value >= 0 ? Math.max(0, usage - value) : 0;
  return {
    isLowering: Number.isFinite(value) && quota?.effective != null && value < quota.effective,
    excess,
    overLimit: excess > 0,
  };
};

export const templateRef = (template = {}) => {
  const name = template.name || template.template_name || 'Untitled template';
  const version = template.version ?? template.version_number;
  return version == null ? name : `${name} — Version ${version}`;
};

export const templateDiff = (left = {}, right = {}) => {
  left = left || {};
  right = right || {};
  const a = Object.fromEntries(templateLimitRows(left).map((q) => [q.quota_key || q.key, q]));
  const b = Object.fromEntries(templateLimitRows(right).map((q) => [q.quota_key || q.key, q]));
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].sort().map((key) => ({
    key,
    name: b[key]?.name || a[key]?.name || key,
    before: a[key]?.value ?? a[key]?.limit ?? null,
    after: b[key]?.value ?? b[key]?.limit ?? null,
    changed: (a[key]?.value ?? a[key]?.limit ?? null) !== (b[key]?.value ?? b[key]?.limit ?? null),
  }));
};
